import { useState, useEffect } from 'react';
import { getUnitCashDrawerStatus } from '../../storage/Storage';
import { HitApi } from '../../Api/ApiHit';
import { searchCashBalance, searchCashTransaction } from '../../constant/Constant';
import toast from 'react-hot-toast';
import TransactionHistory from './TransactionHistory';
import CashModal from './CashModal';
import { exportToCSV, exportToPDF } from '../../utils/exportUtils';

function Expense() {
  // State for unit and cash drawer status
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [hasCashDrawer, setHasCashDrawer] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  
  // Cash balance API state
  const [serverCashBalance, setServerCashBalance] = useState(null);
  const [isLoadingBalance, setIsLoadingBalance] = useState(false);
  
  // Transaction refresh trigger
  const [transactionRefreshTrigger, setTransactionRefreshTrigger] = useState(0);

  // Store active filters from TransactionHistory
  const [activeFilters, setActiveFilters] = useState(null);

  // State for denomination counts
  const [counts, setCounts] = useState({
    note500: '',
    note200: '',
    note100: '',
    note50: '',
    note10: ''
  });

  // Modal state
  const [showCashModal, setShowCashModal] = useState(false);

  // Check unit cash drawer status on component mount
  useEffect(() => {
    const checkUnitCashDrawer = () => {
      try {
        setIsLoading(true);
        const drawerStatus = getUnitCashDrawerStatus();
        
        setSelectedUnit(drawerStatus.unit);
        setHasCashDrawer(drawerStatus.hasDrawer);
        
        console.log('Unit cash drawer status:', {
          unitName: drawerStatus.unit?.unitName || 'N/A',
          hasDrawer: drawerStatus.hasDrawer,
          reason: drawerStatus.reason,
          drawerSettings: drawerStatus.drawerSettings,
          drawerEnabled: drawerStatus.drawerEnabled
        });
      } catch (error) {
        console.error('Error checking unit cash drawer status:', error);
        setHasCashDrawer(false);
        setSelectedUnit(null);
      } finally {
        setIsLoading(false);
      }
    };

    checkUnitCashDrawer();
  }, []);

  // Fetch cash balance from server
  const fetchCashBalance = async () => {
    if (!selectedUnit?._id) {
      console.warn('No unit selected, cannot fetch cash balance');
      return;
    }

    setIsLoadingBalance(true);
    try {
      const json = {
        page: 1,
        limit: 1,
        search: {
          unitId: selectedUnit._id
        }
      };

      console.log('Fetching cash balance for unit:', selectedUnit._id);
      const response = await HitApi(json, searchCashBalance);
      
      if (response?.statusCode === 200 && response?.data?.docs?.length > 0) {
        const balance = response.data.docs[0];
        setServerCashBalance(balance);
        
        // Update local counts with server data
        setCounts({
          note500: balance.note500 || '',
          note200: balance.note200 || '',
          note100: balance.note100 || '',
          note50: balance.note50 || '',
          note10: balance.note10 || ''
        });
        
        console.log('Cash balance fetched successfully:', balance);
        toast.success(`Cash balance loaded: ₹${balance.totalAmount || 0}`);
      } else {
        console.log('No cash balance found for this unit');
        setServerCashBalance(null);
        toast('No existing cash balance found for this unit', {
          icon: 'ℹ️',
          style: {
            background: '#3b82f6',
            color: '#fff',
          },
        });
      }
    } catch (error) {
      console.error('Error fetching cash balance:', error);
      toast.error('Failed to fetch cash balance');
    } finally {
      setIsLoadingBalance(false);
    }
  };


  // Load cash balance when unit is available
  useEffect(() => {
    if (selectedUnit && hasCashDrawer) {
      fetchCashBalance();
    }
  }, [selectedUnit, hasCashDrawer]);



  // Calculate totals
  const totals = {
    note500: (counts.note500 || 0) * 500,
    note200: (counts.note200 || 0) * 200,
    note100: (counts.note100 || 0) * 100,
    note50: (counts.note50 || 0) * 50,
    note10: (counts.note10 || 0) * 10
  };

  const grandTotal = Object.values(totals).reduce((sum, value) => sum + value, 0);

  // Export functionality
  const handleExportData = async (format) => {
    try {
      toast.loading('Preparing export...');

      if (!selectedUnit?._id) {
        toast.dismiss();
        toast.error('No unit selected for export');
        return;
      }

      // Use active filters from TransactionHistory if available
      const searchCriteria = activeFilters ? { ...activeFilters } : { unitId: selectedUnit._id };

      // Fetch all transaction data with limit 1000
      const json = {
        page: 1,
        limit: 1000,
        search: searchCriteria,
        sort: { createdAt: -1 }
      };

      console.log('Exporting with filters:', searchCriteria);
      const res = await HitApi(json, searchCashTransaction);

      if (res?.statusCode === 200 && res?.data?.docs) {
        const allData = res.data.docs;

        if (allData.length === 0) {
          toast.dismiss();
          toast.error('No transaction data to export');
          return;
        }

        const exportHeaders = [
          { title: 'S.No', key: 'serialNumber' },
          { title: 'Date', key: 'date' },
          { title: 'Time', key: 'time' },
          { title: 'Transaction Type', key: 'transactionType' },
          { title: 'Category', key: 'category' },
          { title: 'Description', key: 'description' },
          { title: 'Account To', key: 'accountTo' },
          { title: 'Recorded By', key: 'recordedBy' },
          { title: '₹500 Notes', key: 'note500' },
          { title: '₹200 Notes', key: 'note200' },
          { title: '₹100 Notes', key: 'note100' },
          { title: '₹50 Notes', key: 'note50' },
          { title: '₹10 Notes', key: 'note10' },
          { title: 'Total Amount', key: 'totalAmount' },
          { title: 'Previous Balance', key: 'previousBalance' },
          { title: 'New Balance', key: 'newBalance' }
        ];

        // Format the date and time
        const formatDate = (timestamp) => {
          if (!timestamp) return 'N/A';
          const date = new Date(timestamp);
          return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
          });
        };

        const formatTime = (timestamp) => {
          if (!timestamp) return 'N/A';
          const date = new Date(timestamp);
          return date.toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: true
          });
        };

        // Flatten and format data for export
        const flattenedData = allData.map((row, index) => {
          return {
            serialNumber: index + 1,
            date: formatDate(row.createdAt),
            time: formatTime(row.createdAt),
            transactionType: row.transactionType === 'in' ? 'Cash In' : 'Cash Out',
            category: row.category ? row.category.charAt(0).toUpperCase() + row.category.slice(1) : 'N/A',
            description: row.description || 'No description',
            accountTo: row.inAccountTo?.name || 'N/A',
            recordedBy: row.recordedBy?.name || 'N/A',
            note500: row.note500 || 0,
            note200: row.note200 || 0,
            note100: row.note100 || 0,
            note50: row.note50 || 0,
            note10: row.note10 || 0,
            totalAmount: Number(row.totalAmount || 0).toFixed(2),
            previousBalance: Number(row.previousBalance || 0).toFixed(2),
            newBalance: Number(row.newBalance || 0).toFixed(2)
          };
        });

        // Build filename with filter info
        let filenamePrefix = 'cash_transactions';
        if (activeFilters?.transactionType) {
          filenamePrefix += `_${activeFilters.transactionType}`;
        }
        if (activeFilters?.category && activeFilters.category !== 'all') {
          filenamePrefix += `_${activeFilters.category}`;
        }
        const filename = `${filenamePrefix}_${selectedUnit.unitName}_${new Date().toISOString().split('T')[0]}`;

        toast.dismiss();

        // Create success message based on filters
        const filterInfo = activeFilters && Object.keys(activeFilters).length > 1 ? ' (filtered)' : '';

        if (format === 'csv') {
          exportToCSV(flattenedData, exportHeaders, `${filename}.csv`);
          toast.success(`Exported ${allData.length} transactions${filterInfo} to CSV`);
        } else if (format === 'pdf') {
          exportToPDF(flattenedData, exportHeaders, `${filename}.pdf`, `Cash Transactions - ${selectedUnit.unitName}`);
          toast.success(`Exported ${allData.length} transactions${filterInfo} to PDF`);
        }
      } else {
        toast.dismiss();
        toast.error('Failed to fetch transaction data for export');
      }
    } catch (error) {
      toast.dismiss();
      toast.error('Error exporting data');
      console.error('Export error:', error);
    }
  };


  // Loading component
  const LoadingComponent = () => (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-center items-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Checking unit configuration...</p>
          </div>
        </div>
      </div>
    </div>
  );

  // No cash drawer component
  const NoCashDrawerComponent = () => (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto">
        {/* Header Section */}
        <div className="mb-8">
          <h1 className="text-2xl font-semibold text-gray-800 mb-2">Cash Management Dashboard</h1>
          <p className="text-gray-500">Monitor and manage your cash drawer denominations</p>
        </div>

        {/* No Cash Drawer Message */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="p-12 text-center">
            <div className="w-24 h-24 mx-auto mb-6 bg-yellow-100 rounded-full flex items-center justify-center">
              <svg className="w-12 h-12 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.732 14.5c-.77.833.192 2.5 1.732 2.5z"></path>
              </svg>
            </div>
            
            <h2 className="text-2xl font-semibold text-gray-800 mb-4">Cash Drawer Required</h2>
            <p className="text-gray-600 mb-6 max-w-md mx-auto">
              This unit doesn't have a cash drawer configured. You need to buy and configure a smart cash drawer to access cash management features.
            </p>
            
            {/* Unit Information */}
            <div className="bg-gray-50 rounded-lg p-6 mb-8 max-w-md mx-auto">
              <h3 className="text-lg font-medium text-gray-800 mb-4">Current Unit</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Unit Name:</span>
                  <span className="font-medium text-gray-800">{selectedUnit?.unitName || 'N/A'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Unit Code:</span>
                  <span className="font-medium text-gray-800">{selectedUnit?.unitCode || 'N/A'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Cash Drawer:</span>
                  <span className="text-red-600 font-medium">Not Configured</span>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="space-y-4 sm:space-y-0 sm:space-x-4 sm:flex sm:justify-center">
              <button 
                onClick={() => window.open('https://example.com/smart-drawer', '_blank')}
                className="w-full sm:w-auto px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
              >
                🛒 Buy Smart Cash Drawer
              </button>
            </div>

            {/* Features List */}
            <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6 max-w-3xl mx-auto">
              <div className="text-center">
                <div className="w-12 h-12 mx-auto mb-3 bg-green-100 rounded-lg flex items-center justify-center">
                  <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1"></path>
                  </svg>
                </div>
                <h4 className="font-medium text-gray-800 mb-2">Automatic Cash Counting</h4>
                <p className="text-sm text-gray-600">Smart sensors count denominations automatically</p>
              </div>
              
              <div className="text-center">
                <div className="w-12 h-12 mx-auto mb-3 bg-blue-100 rounded-lg flex items-center justify-center">
                  <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"></path>
                  </svg>
                </div>
                <h4 className="font-medium text-gray-800 mb-2">Secure Access</h4>
                <p className="text-sm text-gray-600">Biometric and PIN-based security features</p>
              </div>
              
              <div className="text-center">
                <div className="w-12 h-12 mx-auto mb-3 bg-purple-100 rounded-lg flex items-center justify-center">
                  <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"></path>
                  </svg>
                </div>
                <h4 className="font-medium text-gray-800 mb-2">Real-time Reporting</h4>
                <p className="text-sm text-gray-600">Live cash flow tracking and analytics</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );


  // Conditional rendering based on loading and cash drawer status
  if (isLoading) {
    return <LoadingComponent />;
  }

  if (!hasCashDrawer) {
    return <NoCashDrawerComponent />;
  }

  // Main cash management component (only shown when cash drawer is available)
  return (
    <div className="p-5">
      <div >
        {/* Header Section */}
        <div className="mb-8 flex justify-between items-start">
          <div>
            <h1 className="text-2xl font-semibold text-gray-800 mb-2">Cash Management Dashboard</h1>
            <p className="text-gray-500">Monitor and manage your cash drawer denominations</p>
            {selectedUnit && (
              <div className="mt-2 text-sm text-gray-600">
                <span>Unit: <span className="font-medium">{selectedUnit.unitName}</span></span>
                <span className="ml-4">Cash Drawer: <span className="text-green-600 font-medium">✓ Enabled</span></span>
              </div>
            )}
          </div>
          <div className="flex gap-2">
            {/* Export Dropdown */}
            <div className="relative group">
              <button
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors text-sm font-medium shadow-sm flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Export Data
                {/* {activeFilters && Object.keys(activeFilters).length > 1 && (
                  <span className="ml-1 px-1.5 py-0.5 bg-white text-green-700 rounded text-xs font-bold">
                    Filtered
                  </span>
                )} */}
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {/* Dropdown Menu */}
              <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg border border-gray-200 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-10">
                <div className="py-1">
                  <button
                    onClick={() => handleExportData('csv')}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                  >
                    <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    Export to Excel (CSV)
                  </button>
                  <button
                    onClick={() => handleExportData('pdf')}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                  >
                    <svg className="w-4 h-4 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                    </svg>
                    Export to PDF
                  </button>
                </div>
              </div>
            </div>

            <button
              onClick={() => setShowCashModal(true)}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm font-medium shadow-sm flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
              Cash Management
            </button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          {/* Total Cash Card */}
          <div className="bg-white rounded-lg shadow-sm p-6 border border-green-200 bg-gradient-to-r from-green-50 to-emerald-50">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                  <span className="text-green-600 font-medium">₹</span>
                </div>
              </div>
              <div className="ml-4">
                <h3 className="text-sm font-medium text-green-700">Total Cash</h3>
                <p className="text-xl font-semibold text-green-800">₹{grandTotal.toLocaleString()}</p>
              </div>
            </div>
          </div>

          {/* Total Notes Card */}
          <div className="bg-white rounded-lg shadow-sm p-6 border border-blue-200 bg-gradient-to-r from-blue-50 to-indigo-50">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                  <span className="text-blue-600 font-medium">#</span>
                </div>
              </div>
              <div className="ml-4">
                <h3 className="text-sm font-medium text-blue-700">Total Notes</h3>
                <p className="text-xl font-semibold text-blue-800">{Object.values(counts).reduce((sum, count) => sum + (count || 0), 0)}</p>
              </div>
            </div>
          </div>

          {/* Last Updated Card */}
          <div className="bg-white rounded-lg shadow-sm p-6 border border-purple-200 bg-gradient-to-r from-purple-50 to-violet-50">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                  <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
              </div>
              <div className="ml-4">
                <h3 className="text-sm font-medium text-purple-700">Last Updated</h3>
                <p className="text-xl font-semibold text-purple-800">
                  {serverCashBalance?.updatedAt ? 
                    new Date(serverCashBalance.updatedAt).toLocaleTimeString() : 
                    'Never'
                  }
                </p>
                {serverCashBalance?.updatedAt && (
                  <p className="text-xs text-purple-500">
                    {new Date(serverCashBalance.updatedAt).toLocaleDateString()}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Denomination Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
          {/* ₹500 Card */}
          <div className="bg-white rounded-lg shadow-sm overflow-hidden border border-orange-200 hover:shadow-md transition-shadow">
            <div className="bg-gradient-to-r from-orange-100 to-amber-100 p-4 border-b border-orange-200">
              <h3 className="text-orange-700 font-medium text-center">₹500</h3>
            </div>
            <div className="p-4">
              <div className="text-center">
                <p className="text-3xl font-bold text-orange-800 mb-2">₹{totals.note500.toLocaleString()}</p>
                <p className="text-sm text-orange-600 mb-1">{counts.note500 || 0} notes</p>
              </div>
            </div>
          </div>

          {/* ₹200 Card */}
          <div className="bg-white rounded-lg shadow-sm overflow-hidden border border-pink-200 hover:shadow-md transition-shadow">
            <div className="bg-gradient-to-r from-pink-100 to-rose-100 p-4 border-b border-pink-200">
              <h3 className="text-pink-700 font-medium text-center">₹200</h3>
            </div>
            <div className="p-4">
              <div className="text-center">
                <p className="text-3xl font-bold text-pink-800 mb-2">₹{totals.note200.toLocaleString()}</p>
                <p className="text-sm text-pink-600 mb-1">{counts.note200 || 0} notes</p>
              </div>
            </div>
          </div>

          {/* ₹100 Card */}
          <div className="bg-white rounded-lg shadow-sm overflow-hidden border border-teal-200 hover:shadow-md transition-shadow">
            <div className="bg-gradient-to-r from-teal-100 to-cyan-100 p-4 border-b border-teal-200">
              <h3 className="text-teal-700 font-medium text-center">₹100</h3>
            </div>
            <div className="p-4">
              <div className="text-center">
                <p className="text-3xl font-bold text-teal-800 mb-2">₹{totals.note100.toLocaleString()}</p>
                <p className="text-sm text-teal-600 mb-1">{counts.note100 || 0} notes</p>
              </div>
            </div>
          </div>

          {/* ₹50 Card */}
          <div className="bg-white rounded-lg shadow-sm overflow-hidden border border-emerald-200 hover:shadow-md transition-shadow">
            <div className="bg-gradient-to-r from-emerald-100 to-green-100 p-4 border-b border-emerald-200">
              <h3 className="text-emerald-700 font-medium text-center">₹50</h3>
            </div>
            <div className="p-4">
              <div className="text-center">
                <p className="text-3xl font-bold text-emerald-800 mb-2">₹{totals.note50.toLocaleString()}</p>
                <p className="text-sm text-emerald-600 mb-1">{counts.note50 || 0} notes</p>
              </div>
            </div>
          </div>

          {/* ₹10 Card */}
          <div className="bg-white rounded-lg shadow-sm overflow-hidden border border-indigo-200 hover:shadow-md transition-shadow">
            <div className="bg-gradient-to-r from-indigo-100 to-blue-100 p-4 border-b border-indigo-200">
              <h3 className="text-indigo-700 font-medium text-center">₹10</h3>
            </div>
            <div className="p-4">
              <div className="text-center">
                <p className="text-3xl font-bold text-indigo-800 mb-2">₹{totals.note10.toLocaleString()}</p>
                <p className="text-sm text-indigo-600 mb-1">{counts.note10 || 0} notes</p>
              </div>
            </div>
          </div>
        </div>


        {/* Cash Management Modal */}
        <CashModal
          isOpen={showCashModal}
          onClose={() => setShowCashModal(false)}
          selectedUnit={selectedUnit}
          currentBalance={grandTotal}
          currentCounts={counts}
          onTransactionComplete={(transactionData) => {
            // Handle new transaction structure with multiple transactions
            setCounts(prev => {
              let newCounts = { ...prev };
              
              // Process cash in transaction if present
              if (transactionData.cashIn) {
                const cashIn = transactionData.cashIn.denominations;
                newCounts = {
                  note500: (newCounts.note500 || 0) + (cashIn.note500 || 0),
                  note200: (newCounts.note200 || 0) + (cashIn.note200 || 0),
                  note100: (newCounts.note100 || 0) + (cashIn.note100 || 0),
                  note50: (newCounts.note50 || 0) + (cashIn.note50 || 0),
                  note10: (newCounts.note10 || 0) + (cashIn.note10 || 0)
                };
              }
              
              // Process cash out transaction if present
              if (transactionData.cashOut) {
                const cashOut = transactionData.cashOut.denominations;
                newCounts = {
                  note500: Math.max(0, (newCounts.note500 || 0) - (cashOut.note500 || 0)),
                  note200: Math.max(0, (newCounts.note200 || 0) - (cashOut.note200 || 0)),
                  note100: Math.max(0, (newCounts.note100 || 0) - (cashOut.note100 || 0)),
                  note50: Math.max(0, (newCounts.note50 || 0) - (cashOut.note50 || 0)),
                  note10: Math.max(0, (newCounts.note10 || 0) - (cashOut.note10 || 0))
                };
              }
              
              return newCounts;
            });
            
            // Trigger transaction list refresh
            setTransactionRefreshTrigger(prev => prev + 1);
          }}
        />

        {/* Transaction History Section */}
        <div className="mt-8">
          <TransactionHistory
            selectedUnit={selectedUnit}
            refreshTrigger={transactionRefreshTrigger}
            onFiltersChange={setActiveFilters}
          />
        </div>
      </div>
    </div>
  );
}

export default Expense;